import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useResetPassword } from '@field-tickets/shared';
import { Spinner } from '@/components/Spinner';

/**
 * Supabase password-reset emails can deliver the recovery token in two
 * different shapes depending on the project's auth flow configuration:
 *
 *   1. PKCE flow      → `?code=<one-time-code>` in the query string
 *   2. Implicit/legacy → `#access_token=<jwt>&type=recovery&...` in the fragment
 *
 * The backend `/api/auth/reset-password` endpoint accepts either value via the
 * `code` field — for PKCE it does `exchangeCodeForSession(code)`, for the
 * implicit flow the JWT itself is enough to identify the user. We surface
 * whichever is present.
 */
function readResetTokenFromUrl(searchParams: URLSearchParams): string | null {
  const queryCode = searchParams.get('code');
  if (queryCode) return queryCode;

  if (typeof window === 'undefined') return null;
  const hash = window.location.hash || '';
  if (!hash) return null;

  const fragmentParams = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
  return (
    fragmentParams.get('code') ||
    fragmentParams.get('access_token') ||
    null
  );
}

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Lazy initialiser reads the token synchronously on first render so the
  // "Invalid Link" screen never flickers in before the value is resolved.
  const [code] = useState<string | null>(() => readResetTokenFromUrl(searchParams));
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const mutation = useResetPassword();

  // Strip the fragment from browser history after reading an implicit-flow
  // token so the raw access_token doesn't persist in the history stack.
  useEffect(() => {
    if (typeof window !== 'undefined' && window.location.hash && code) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  }, [code]);

  if (code === null) {
    return (
      <div className="login-page">
        <h1 className="login-title">Invalid link</h1>
        <p className="login-subtitle" style={{ maxWidth: 320 }} role="alert">
          This password reset link is invalid or has expired.
        </p>
        <Link
          to="/forgot-password"
          className="login-btn"
          style={{ textDecoration: 'none', display: 'inline-block', maxWidth: 320, width: '100%', textAlign: 'center', marginTop: 'var(--space-4)' }}
        >
          Request a New Link
        </Link>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    try {
      const result = await mutation.mutateAsync({ code, password });
      if (result.noAccess) {
        navigate('/no-access', { replace: true });
      } else if (result.pendingApproval) {
        navigate('/pending-approval', { replace: true });
      } else if (result.suspended) {
        navigate('/no-access', { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to reset password';
      setError(msg);
    }
  };

  return (
    <div className="login-page">
      <svg className="login-logo" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
        <rect x="2" y="3" width="20" height="14" rx="2" />
        <line x1="8" y1="21" x2="16" y2="21" />
        <line x1="12" y1="17" x2="12" y2="21" />
      </svg>

      <h1 className="login-title">Set new password</h1>
      <p className="login-subtitle">Choose a new password for your account.</p>

      {error && (
        <div role="alert" style={{ color: 'var(--color-error)', marginBottom: 'var(--space-4)', fontSize: 'var(--text-sm)' }}>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} style={{ width: '100%', maxWidth: 320 }}>
        <div className="form-group">
          <label htmlFor="password">New Password</label>
          <input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Minimum 8 characters"
            required
            autoComplete="new-password"
            minLength={8}
            autoFocus
          />
        </div>

        <div className="form-group">
          <label htmlFor="confirmPassword">Confirm Password</label>
          <input
            id="confirmPassword"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Re-enter password"
            required
            autoComplete="new-password"
            minLength={8}
          />
        </div>

        <button
          type="submit"
          className="login-btn"
          disabled={mutation.isPending}
          aria-busy={mutation.isPending}
          style={{ marginBottom: 'var(--space-3)' }}
        >
          {mutation.isPending ? <Spinner size="sm" /> : 'Reset Password'}
        </button>
      </form>

      <Link
        to="/login"
        style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)' }}
      >
        Back to Sign In
      </Link>
    </div>
  );
}
